import * as React from 'react';
import { FunctionComponent, useContext, useEffect, useState, Dispatch, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import MiniSearch from 'minisearch';

// Openlayers
import OlSourceVector from 'ol/source/Vector';
import Geometry from 'ol/geom/Geometry';
import OlFeature from 'ol/Feature';
import OlFormatWKT from 'ol/format/WKT';
import { ProjectionLike } from 'ol/proj';

// MUI
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

// MUI Icons
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';
import ExploreIcon from '@mui/icons-material/Explore';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

// Context
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';

// Lib
import DataController from '@/lib/DataController';

// Models
import modelToponimi from '@/models/toponimi';
import modelToponimTipovi from '@/models/toponim_tipovi';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { IMenuState } from '@/@types/ui/Table';

const dcTipovi = new DataController(modelToponimTipovi);
const dcToponimi = new DataController(modelToponimi);

export interface SearchPaneProps {
  setSelectedSource: Dispatch<React.SetStateAction<OlSourceVector<OlFeature<Geometry>>>>;
  searchItemsListDisplayLimit?: number;
}

const SearchPane: FunctionComponent<any> = (props: SearchPaneProps) => {
  const { setSelectedSource, searchItemsListDisplayLimit } = props;

  const { t } = useTranslation();
  const mapContext = useContext(MapContext);
  const { map } = mapContext as MapContextType;
  const tenantContext = useContext(TenantContext);
  const { mapSettings } = tenantContext as TenantContextType;

  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<string>('');
  const [results, setResults] = useState<DCRecord[]>([]);
  const [mwResults, setMwResults] = useState<DCRecord[]>([]);
  const [mwLoading, setMwLoading] = useState<boolean>(false);
  const [menuState, setMenuState] = useState<IMenuState>({
    open: false,
    anchorEl: null,
  });
  const [hiddenTypes, setHiddenTypes] = useState<Array<Number> | null>(null);
  const [toponimTipoviDisplayLimits, setToponimTipoviDisplayLimits] = useState<{
    [key: number]: number;
  } | null>(null);

  const preparedRef = useRef<any[]>([]);
  const miniSearchRef = useRef<MiniSearch | null>(null);

  // #region Queries

  const { data: toponimTipovi = [] } = useQuery<DCRecord[]>({
    queryKey: ['toponim-tipovi'],
    queryFn: async () => {
      const resp = await dcTipovi.GetData();
      return resp.success && Array.isArray(resp.data) ? resp.data : [];
    },
  });

  const feTypeIds = React.useMemo(
    () => new Set(toponimTipovi.filter((tip: DCRecord) => !tip.search_using_mw).map((tip: DCRecord) => tip.id as number)),
    [toponimTipovi]
  );
  const mwTypeIds = React.useMemo(
    () => new Set(toponimTipovi.filter((tip: DCRecord) => tip.search_using_mw).map((tip: DCRecord) => tip.id as number)),
    [toponimTipovi]
  );

  const { data: allFeRecords = [], isFetching: feLoading } = useQuery<DCRecord[]>({
    queryKey: ['toponimi-fe'],
    queryFn: async () => {
      const resp = await dcToponimi.GetData();
      return resp.success && Array.isArray(resp.data) ? resp.data : [];
    },
    enabled: feTypeIds.size > 0,
  });

  const records = React.useMemo(
    () => allFeRecords.filter((r: DCRecord) => feTypeIds.has(r.toponim_tip_id as number)),
    [allFeRecords, feTypeIds]
  );

  // #endregion

  // #region useEffect

  useEffect(() => {
    refreshToponimTipoviDisplayLimits();
  }, [toponimTipovi]);

  useEffect(() => {
    if (toponimTipovi.length) {
      loadHiddenTypesFromStorage();
    }
  }, [toponimTipovi]);

  // Focus search upon entry into component
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [toponimTipovi]);

  // Build MiniSearch index whenever FE records change
  useEffect(() => {
    if (!records.length) return;

    preparedRef.current = records.map((r: DCRecord, i: number) => {
      const naziv = normalize(r.naziv);
      const opis = normalize(r.opis);
      return {
        id: i,
        record: r,
        naziv,
        opis,
        tokens: `${naziv} ${opis}`.split(/\s+/),
      };
    });

    miniSearchRef.current = new MiniSearch({
      fields: ['naziv', 'opis'],
      storeFields: ['id'],
      searchOptions: { boost: { naziv: 2, opis: 1 }, prefix: true, fuzzy: 0 },
    });
    miniSearchRef.current.addAll(preparedRef.current);
  }, [records]);

  // FE filter — synchronous on value change
  useEffect(() => {
    if (value.length === 0) {
      refreshToponimTipoviDisplayLimits();
      setResults(records);
      return;
    }
    setResults(filterResults());
  }, [value, records]);

  // MW debounced search — fires for MW types when value >= 2 chars
  useEffect(() => {
    if (mwTypeIds.size === 0) return undefined;

    if (value.length < 2) {
      setMwResults([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      reloadMwToponimi(value);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value]);

  // Save hiddenTypes to localstorage upon user settings change
  useEffect(() => {
    if (Array.isArray(hiddenTypes)) {
      saveHiddenTypesToStorage();
    }
  }, [hiddenTypes]);

  // #endregion

  // #region Functions

  const deaccent = (val: string) => {
    let r = val.toLowerCase();
    r = r.replace(/[ăáä]/g, 'a');
    r = r.replace(/[čć]/g, 'c');
    r = r.replace(/[đ]/g, 'd');
    r = r.replace(/[ëė]/g, 'e');
    r = r.replace(/[í]/g, 'i');
    r = r.replace(/[ń]/g, 'n');
    r = r.replace(/[öóő]/g, 'o');
    r = r.replace(/[šś]/g, 's');
    r = r.replace(/[üúű]/g, 'u');
    r = r.replace(/[ž]/g, 'z');
    return r;
  };

  const normalize = (str: any) =>
    deaccent(str || '')
      .toLowerCase()
      .replaceAll('-', ' ');

  const refreshToponimTipoviDisplayLimits = () => {
    if (toponimTipovi.length) {
      const res: { [key: number]: number } = {};
      toponimTipovi.forEach((toponimTip: DCRecord) => {
        res[toponimTip.id as number] = searchItemsListDisplayLimit || 20;
      });
      setToponimTipoviDisplayLimits(res);
    }
  };

  const loadHiddenTypesFromStorage = () => {
    try {
      const hiddenTypesStringified = localStorage.getItem('hidden_search_toponyms');
      if (hiddenTypesStringified) {
        const parsedHiddenTypes = JSON.parse(hiddenTypesStringified);
        if (Array.isArray(parsedHiddenTypes) && parsedHiddenTypes.every((x: unknown) => typeof x === 'number')) {
          const resultingHiddenTypes = toponimTipovi
            .slice()
            .filter((x: DCRecord) => parsedHiddenTypes.indexOf(x.id as number) !== -1)
            .map((x: DCRecord) => x.id as number);
          setHiddenTypes(resultingHiddenTypes);
        } else {
          // eslint-disable-next-line no-console
          console.warn('Invalid hiddenTypes data in localStorage, resetting to default.');
          setHiddenTypes([]);
        }
      } else {
        setHiddenTypes([]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing hiddenTypes from localStorage', error);
      setHiddenTypes([]);
    }
  };

  const saveHiddenTypesToStorage = () => {
    try {
      localStorage.setItem('hidden_search_toponyms', JSON.stringify(hiddenTypes));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving hiddenTypes to localStorage', error);
    }
  };

  const filterResults = () => {
    if (!value || !miniSearchRef.current) return records;
    const miniSearch = miniSearchRef.current;

    const input = normalize(value);
    if(!input.trim()) return records;

    const tokens = input.split(/\s+/).filter(Boolean);

    const resultsPerToken = tokens.map((token) => miniSearch.search(token));

    const miniResults = resultsPerToken.reduce((a, b) => a.filter((x) => b.some((y) => y.id === x.id)));

    return miniResults
      .map((r) => {
        const item = preparedRef.current[r.id];
        let scoreBoost = 0;

        const inputFreq = tokens.reduce((acc, tok) => {
          acc.set(tok, (acc.get(tok) || 0) + 1);
          return acc;
        }, new Map<string, number>());

        const textFreq = (item.tokens as string[]).reduce((acc, tok) => {
          acc.set(tok, (acc.get(tok) || 0) + 1);
          return acc;
        }, new Map<string, number>());

        let fullMatch = true;
        inputFreq.forEach((count, token) => {
          const textCount = textFreq.get(token) || 0;
          if (textCount < count) fullMatch = false;

          // 50 bodova za svaki potpuno poklopljeni token
          scoreBoost += Math.min(textCount, count) * 50;
          // 30 bodova za prefix
          textFreq.forEach((tc, textToken) => {
            if (textToken.startsWith(token) && textToken !== token) {
              scoreBoost += tc * 30;
            }
          });
        });

        if (fullMatch) scoreBoost += 200;

        // prednost kraci rez
        scoreBoost -= item.naziv.length * 1.1;

        return { record: item.record, finalScore: r.score + scoreBoost };
      })
      .sort((a, b) => b.finalScore - a.finalScore)
      .map((x) => x.record);
  };

  const reloadMwToponimi = (searchString: string) => {
    setMwLoading(true);
    dcToponimi
      .GetData(`core/toponimi-search?q=${encodeURIComponent(searchString)}`)
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setMwResults(resp.data.filter((r: DCRecord) => mwTypeIds.has(r.toponim_tip_id as number)));
        } else {
          setMwResults([]);
        }
      })
      .finally(() => {
        setMwLoading(false);
      });
  };

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setValue(evt.target.value);
  };

  const handleExplore = (evt: React.MouseEvent<HTMLButtonElement>, item: DCRecord, toponim_id: number) => {
    const { wkt } = item;
    if (wkt) {
      const proj = item.proj || 'EPSG:3765';
      const wktFormatter = new OlFormatWKT();
      const feat = wktFormatter.readFeature(wkt, {
        // dataProjection: "EPSG:3765" // piece of shit code luka // sorry luka comment was made in the heat of the moment
        dataProjection: proj as ProjectionLike,
        featureProjection: 'EPSG:3857',
      });

      setSelectedSource(new OlSourceVector({ features: [feat] as OlFeature<Geometry>[] }));

      const toponimTip = toponimTipovi.find((tpTip: DCRecord) => tpTip.id === toponim_id);

      setTimeout(() => {
        const extent = feat.getGeometry()?.getExtent();
        if (map && extent) {
          map.getView().fit(extent, {
            padding: [10, 10, 10, 220],
            duration: 500,
            maxZoom: toponimTip?.max_zoom
              ? (toponimTip.max_zoom as number)
              : mapSettings?.map_max_view_zoom
              ? mapSettings.map_max_view_zoom
              : 20,
          });
        }
      }, 10);
    }
  };

  const handleOpenSearchSettings = (evt: React.MouseEvent<HTMLButtonElement>) => {
    setMenuState({ open: true, anchorEl: evt.currentTarget });
  };

  const handleCloseSearchSettings = () => {
    setMenuState({ open: false, anchorEl: null });
  };

  const handleToggleToponimTip = (evt: any, tipId: number) => {
    if (hiddenTypes) {
      if (hiddenTypes.includes(tipId)) {
        setHiddenTypes(hiddenTypes.filter((x) => x !== tipId));
      } else {
        setHiddenTypes(hiddenTypes.concat([tipId]));
      }
    }
  };

  const loadMoreItems = (toponimTipId: number) => {
    if (toponimTipoviDisplayLimits?.[toponimTipId] != null) {
      setToponimTipoviDisplayLimits((prev) => ({
        ...prev,
        [toponimTipId]: (prev?.[toponimTipId] ?? 0) + (searchItemsListDisplayLimit || 20),
      }));
    }
  };

  const icons: { [key: string]: React.ReactNode } = {
    '1': <HomeIcon color="disabled" />,
    '2': <AccountBoxIcon color="disabled" />,
    '3': <LanguageIcon color="disabled" />,
    '4': <RequestPageIcon color="disabled" />,
    default: <TravelExploreIcon color="disabled" />,
  };

  const allResults = [...results, ...mwResults];
  const loading = feLoading || mwLoading;
  const isDisabled = toponimTipovi.length === 0 || (feTypeIds.size > 0 && records.length === 0 && mwTypeIds.size === 0);

  // #endregion

  return (
    <>
      <TextField
        inputRef={inputRef}
        sx={{ padding: '10px 20px' }}
        value={value}
        onChange={handleChange}
        disabled={isDisabled}
        fullWidth
        placeholder={t('map:sidebar.search_placeholder') as string}
        InputProps={{
          startAdornment: <InputAdornment position="start">{loading ? <CircularProgress size={20} /> : <SearchIcon />}</InputAdornment>,
          endAdornment: (
            <InputAdornment position="end">
              {loading || toponimTipovi.length === 0 ? null : (
                <>
                  <IconButton onClick={handleOpenSearchSettings}>
                    <SettingsIcon />
                  </IconButton>
                  <Menu
                    open={menuState.open}
                    onClose={handleCloseSearchSettings}
                    anchorEl={menuState.anchorEl}
                    transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                  >
                    {toponimTipovi.map((tip: DCRecord, i: number) => (
                      <MenuItem
                        value={i}
                        onClick={(evt) => handleToggleToponimTip(evt, tip.id as number)}
                        key={`search-menu-item-${tip.id}`}
                      >
                        <Switch color="primary" checked={hiddenTypes ? !hiddenTypes.includes(tip.id as number) : true} value={i} />
                        <Typography variant="caption" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          {t(`map:toponimi.${tip.toponim_tip}` as string)}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </InputAdornment>
          ),
        }}
      />
      <List
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'auto',
          '& ul': { padding: 0 },
        }}
        subheader={<li />}
      >
        {toponimTipovi
          .filter((toponimTip: DCRecord) => !hiddenTypes?.includes(toponimTip.id as number))
          .map((toponimTip: DCRecord) => (
            <li key={`section-${toponimTip.id}`}>
              <ul>
                {allResults.filter((x) => x.toponim_tip_id === toponimTip.id).length > 0 ? (
                  <ListSubheader>
                    {`${t(`map:toponimi.${toponimTip.toponim_tip}` as string)} (${
                      allResults.filter((x) => x.toponim_tip_id === toponimTip.id).length
                    })`}
                  </ListSubheader>
                ) : null}

                {toponimTipoviDisplayLimits?.[toponimTip.id as number] ? (
                  <>
                    {allResults
                      .filter((x) => x.toponim_tip_id === toponimTip.id)
                      .slice(0, toponimTipoviDisplayLimits[toponimTip.id as number])
                      .map((item) => (
                        <ListItem
                          key={`item-${toponimTip.id}-${item.id}`}
                          secondaryAction={
                            <Tooltip title={t('map:controls.zoom_to_selected')}>
                              <IconButton
                                edge="end"
                                aria-label="explore"
                                onClick={(evt) => handleExplore(evt, item, toponimTip.id as number)}
                              >
                                <ExploreIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ backgroundColor: theme.palette.background.default }}>
                              {icons[toponimTip.id as string] != null ? icons[toponimTip.id as string] : icons.default}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${item.naziv_ttoken ? t(item.naziv_ttoken as string) : item.naziv}`}
                            secondary={`${item.opis_ttoken ? t(item.opis_ttoken as string) : item.opis}`}
                          />
                        </ListItem>
                      ))}

                    {allResults.filter((x) => x.toponim_tip_id === toponimTip.id).length >
                    (toponimTipoviDisplayLimits[toponimTip.id as number] as number) ? (
                      <Button
                        onClick={() => loadMoreItems(toponimTip.id as number)}
                        variant="contained"
                        size="small"
                        sx={{ width: '80%', textAlign: 'center', transform: 'translate(10%,0)' }}
                      >
                        {t('buttons.show_more')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </ul>
            </li>
          ))}
      </List>
    </>
  );
};

export default SearchPane;
